/**
 * Kloutforce App Root
 * Main routing configuration using DashboardLayout as the app shell.
 */
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';

import { AppMain, Spinner } from '@bright-web-skeleton/ui';
import { ProtectedRoute, useAuth } from '@bright-web-skeleton/auth';
import { DashboardLayout } from './components/layout';
import { useInviteIQCapture } from '@bright-web-skeleton/shared/inviteiq';
import { useNoIndex } from '@bright-web-skeleton/shared/seo';

import { routes } from './routes';

// Tailwind sanity page
const TailwindSanityPage = lazy(() =>
  import('@bright-web-skeleton/ui').then((m) => ({ default: (m as any).TailwindSanityPage })),
);

// Public pages
const LandingPage = lazy(() =>
  import('./pages/LandingPage').then((m) => ({ default: m.LandingPage })),
);
const AuthPage = lazy(() => import('./pages/AuthPage').then((m) => ({ default: m.AuthPage })));
const AuthCallbackPage = lazy(() =>
  import('./pages/AuthCallbackPage').then((m) => ({ default: m.AuthCallbackPage })),
);
const PrivacyPage = lazy(() =>
  import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() => import('./pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const BrandStorePage = lazy(() =>
  import('./pages/BrandStorePage').then((m) => ({ default: m.BrandStorePage })),
);
const CreatorProfilePage = lazy(() =>
  import('./pages/CreatorProfilePage').then((m) => ({ default: m.CreatorProfilePage })),
);

// Main app pages
const CreatorDashboardPage = lazy(() =>
  import('./pages/CreatorDashboardPage').then((m) => ({ default: m.CreatorDashboardPage })),
);
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const DiscoverPage = lazy(() =>
  import('./pages/DiscoverPage').then((m) => ({ default: m.DiscoverPage })),
);
const CreatePage = lazy(() =>
  import('./pages/CreatePage').then((m) => ({ default: m.CreatePage })),
);
const CreatorDiscoveryPage = lazy(() =>
  import('./pages/CreatorDiscoveryPage').then((m) => ({ default: m.CreatorDiscoveryPage })),
);
const NotificationsPage = lazy(() =>
  import('./pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const KatalogPage = lazy(() =>
  import('./pages/KatalogPage').then((m) => ({ default: m.KatalogPage })),
);
const DealDetailPage = lazy(() =>
  import('./pages/DealDetailPage').then((m) => ({ default: m.DealDetailPage })),
);

// Account pages
const ProfilePage = lazy(() =>
  import('./pages/account/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const ConnectSocialsPage = lazy(() =>
  import('./pages/account/ConnectSocialsPage').then((m) => ({ default: m.ConnectSocialsPage })),
);
const EarningsPage = lazy(() =>
  import('./pages/account/EarningsPage').then((m) => ({ default: m.EarningsPage })),
);
const InvitesPage = lazy(() =>
  import('./pages/account/InvitesPage').then((m) => ({ default: m.InvitesPage })),
);
const WalletPage = lazy(() =>
  import('./pages/account/WalletPage').then((m) => ({ default: m.WalletPage })),
);
const SettingsPage = lazy(() =>
  import('./pages/account/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const PerformancePage = lazy(() =>
  import('./pages/account/PerformancePage').then((m) => ({ default: m.PerformancePage })),
);
const BrandDealsPage = lazy(() =>
  import('./pages/account/BrandDealsPage').then((m) => ({ default: m.BrandDealsPage })),
);
const YourKatalogPage = lazy(() =>
  import('./pages/account/YourKatalogPage').then((m) => ({ default: m.YourKatalogPage })),
);
const KatalogsListPage = lazy(() =>
  import('./pages/account/KatalogsListPage').then((m) => ({ default: m.KatalogsListPage })),
);
const KatalogEditorPage = lazy(() =>
  import('./pages/account/KatalogEditorPage').then((m) => ({ default: m.KatalogEditorPage })),
);
const KatalogOnboardingPage = lazy(() =>
  import('./pages/account/KatalogOnboardingPage').then((m) => ({
    default: m.KatalogOnboardingPage,
  })),
);
const KatalogCreatePage = lazy(() =>
  import('./pages/account/KatalogCreatePage').then((m) => ({ default: m.KatalogCreatePage })),
);
const KatalogDashboardPage = lazy(() =>
  import('./pages/account/KatalogDashboardPage').then((m) => ({ default: m.KatalogDashboardPage })),
);
const PortfolioPage = lazy(() =>
  import('./pages/account/PortfolioPage').then((m) => ({ default: m.PortfolioPage })),
);

// Invite page
const InvitePage = lazy(() =>
  import('./pages/InvitePage').then((m) => ({ default: m.InvitePage })),
);
const OperatorInvitePage = lazy(() =>
  import('./pages/OperatorInvitePage').then((m) => ({ default: m.OperatorInvitePage })),
);
const CreatorInstructionsPage = lazy(() =>
  import('./pages/CreatorInstructionsPage').then((m) => ({ default: m.CreatorInstructionsPage })),
);

// General Agent pages
const GALandingPage = lazy(() =>
  import('./pages/general-agent/GALandingPage').then((m) => ({ default: m.GALandingPage })),
);
const GARequestPage = lazy(() =>
  import('./pages/general-agent/GARequestPage').then((m) => ({ default: m.GARequestPage })),
);
const GAInvitePage = lazy(() =>
  import('./pages/general-agent/GAInvitePage').then((m) => ({ default: m.GAInvitePage })),
);
const GAWelcomePage = lazy(() =>
  import('./pages/general-agent/GAWelcomePage').then((m) => ({ default: m.GAWelcomePage })),
);
const GADashboardPage = lazy(() =>
  import('./pages/general-agent/GADashboardPage').then((m) => ({ default: m.GADashboardPage })),
);
const GAEarningsPage = lazy(() =>
  import('./pages/general-agent/GAEarningsPage').then((m) => ({ default: m.GAEarningsPage })),
);
const GARecruitsPage = lazy(() =>
  import('./pages/general-agent/GARecruitsPage').then((m) => ({ default: m.GARecruitsPage })),
);
const GAPayoutsPage = lazy(() =>
  import('./pages/general-agent/GAPayoutsPage').then((m) => ({ default: m.GAPayoutsPage })),
);
const GAInviteToolsPage = lazy(() =>
  import('./pages/general-agent/GAInviteToolsPage').then((m) => ({ default: m.GAInviteToolsPage })),
);
const GAInstructionsPage = lazy(() =>
  import('./pages/general-agent/GAInstructionsPage').then((m) => ({
    default: m.GAInstructionsPage,
  })),
);
const GAActivatePage = lazy(() =>
  import('./pages/general-agent/GAActivatePage').then((m) => ({ default: m.GAActivatePage })),
);
const GAManageProfilePage = lazy(() =>
  import('./pages/general-agent/GAManageProfilePage').then((m) => ({
    default: m.GAManageProfilePage,
  })),
);
const ActivationReturnPage = lazy(() =>
  import('./pages/program/ActivationReturnPage').then((m) => ({ default: m.ActivationReturnPage })),
);

// Agent activate
const AgentActivatePage = lazy(() =>
  import('./pages/agent/AgentActivatePage').then((m) => ({ default: m.AgentActivatePage })),
);

// Partner activate
const PartnerActivatePage = lazy(() =>
  import('./pages/partner/PartnerActivatePage').then((m) => ({ default: m.PartnerActivatePage })),
);

// Agent pages
const AgentLandingPage = lazy(() =>
  import('./pages/AgentLandingPage').then((m) => ({ default: m.AgentLandingPage })),
);
const AgentApplyPage = lazy(() =>
  import('./pages/AgentApplyPage').then((m) => ({ default: m.AgentApplyPage })),
);
const AgentWelcomePage = lazy(() =>
  import('./pages/agent/AgentWelcomePage').then((m) => ({ default: m.AgentWelcomePage })),
);
const AgentInviteLandingPage = lazy(() =>
  import('./pages/agent/AgentInviteLandingPage').then((m) => ({
    default: m.AgentInviteLandingPage,
  })),
);
const AgentOverviewPage = lazy(() =>
  import('./pages/agent/AgentOverviewPage').then((m) => ({ default: m.AgentOverviewPage })),
);
const AgentRecruitsPage = lazy(() =>
  import('./pages/agent/AgentRecruitsPage').then((m) => ({ default: m.AgentRecruitsPage })),
);
const AgentEarningsPage = lazy(() =>
  import('./pages/agent/AgentEarningsPage').then((m) => ({ default: m.AgentEarningsPage })),
);
const AgentPayoutsPage = lazy(() =>
  import('./pages/agent/AgentPayoutsPage').then((m) => ({ default: m.AgentPayoutsPage })),
);
const AgentInviteToolsPage = lazy(() =>
  import('./pages/agent/AgentInviteToolsPage').then((m) => ({ default: m.AgentInviteToolsPage })),
);
const AgentInstructionsPage = lazy(() =>
  import('./pages/agent/AgentInstructionsPage').then((m) => ({ default: m.AgentInstructionsPage })),
);
const AgentNetworkPage = lazy(() =>
  import('./pages/agent/network/AgentNetworkPage').then((m) => ({ default: m.AgentNetworkPage })),
);
const AgentManageBrandFeePage = lazy(() =>
  import('./pages/agent/AgentManageBrandFeePage').then((m) => ({
    default: m.AgentManageBrandFeePage,
  })),
);
const AgentBillingPage = lazy(() =>
  import('./pages/agent/AgentBillingPage').then((m) => ({ default: m.AgentBillingPage })),
);
const AgentGAAgreementsPage = lazy(() =>
  import('./pages/agent/AgentGAAgreementsPage').then((m) => ({ default: m.AgentGAAgreementsPage })),
);
const AgentManageProfilePage = lazy(() =>
  import('./pages/agent/AgentManageProfilePage').then((m) => ({
    default: m.AgentManageProfilePage,
  })),
);

// Partner pages
const PartnerLandingPage = lazy(() =>
  import('./pages/PartnerLandingPage').then((m) => ({ default: m.PartnerLandingPage })),
);
const PartnerOnboardingPage = lazy(() =>
  import('./pages/PartnerOnboardingPage').then((m) => ({ default: m.PartnerOnboardingPage })),
);
const PartnerUpgradePage = lazy(() =>
  import('./pages/partner/PartnerUpgradePage').then((m) => ({ default: m.PartnerUpgradePage })),
);
const PartnerInviteLandingPage = lazy(() =>
  import('./pages/partner/PartnerInviteLandingPage').then((m) => ({
    default: m.PartnerInviteLandingPage,
  })),
);
const PartnerWelcomePage = lazy(() =>
  import('./pages/partner/PartnerWelcomePage').then((m) => ({ default: m.PartnerWelcomePage })),
);
const PartnerDashboardPage = lazy(() =>
  import('./pages/partner/PartnerDashboardPage').then((m) => ({ default: m.PartnerDashboardPage })),
);
const PartnerEarningsPage = lazy(() =>
  import('./pages/partner/PartnerEarningsPage').then((m) => ({ default: m.PartnerEarningsPage })),
);
const PartnerNetworkPage = lazy(() =>
  import('./pages/partner/PartnerNetworkPage').then((m) => ({ default: m.PartnerNetworkPage })),
);
const PartnerRecruitsPage = lazy(() =>
  import('./pages/partner/PartnerRecruitsPage').then((m) => ({ default: m.PartnerRecruitsPage })),
);
const PartnerPayoutsPage = lazy(() =>
  import('./pages/partner/PartnerPayoutsPage').then((m) => ({ default: m.PartnerPayoutsPage })),
);
const PartnerInviteToolsPage = lazy(() =>
  import('./pages/partner/PartnerInviteToolsPage').then((m) => ({
    default: m.PartnerInviteToolsPage,
  })),
);
const PartnerInstructionsPage = lazy(() =>
  import('./pages/partner/PartnerInstructionsPage').then((m) => ({
    default: m.PartnerInstructionsPage,
  })),
);
const PartnerManageProfilePage = lazy(() =>
  import('./pages/partner/PartnerManageProfilePage').then((m) => ({
    default: m.PartnerManageProfilePage,
  })),
);

// Connector pages
const ConnectorLandingPage = lazy(() =>
  import('./pages/ConnectorLandingPage').then((m) => ({ default: m.ConnectorLandingPage })),
);
const ConnectorOnboardingPage = lazy(() =>
  import('./pages/connector/ConnectorOnboardingPage').then((m) => ({
    default: m.ConnectorOnboardingPage,
  })),
);
const ConnectorWelcomePage = lazy(() =>
  import('./pages/connector/ConnectorWelcomePage').then((m) => ({
    default: m.ConnectorWelcomePage,
  })),
);
const ConnectorDashboardPage = lazy(() =>
  import('./pages/connector/ConnectorDashboardPage').then((m) => ({
    default: m.ConnectorDashboardPage,
  })),
);
const ConnectorEarningsPage = lazy(() =>
  import('./pages/connector/ConnectorEarningsPage').then((m) => ({
    default: m.ConnectorEarningsPage,
  })),
);
const ConnectorRecruitsPage = lazy(() =>
  import('./pages/connector/ConnectorRecruitsPage').then((m) => ({
    default: m.ConnectorRecruitsPage,
  })),
);
const ConnectorPayoutsPage = lazy(() =>
  import('./pages/connector/ConnectorPayoutsPage').then((m) => ({
    default: m.ConnectorPayoutsPage,
  })),
);
const ConnectorInviteLandingPage = lazy(() =>
  import('./pages/connector/ConnectorInviteLandingPage').then((m) => ({
    default: m.ConnectorInviteLandingPage,
  })),
);
const ConnectorManageProfilePage = lazy(() =>
  import('./pages/connector/ConnectorManageProfilePage').then((m) => ({
    default: m.ConnectorManageProfilePage,
  })),
);

// Public role profile pages — resolved from members.primary_handle (Phase F)
const PublicRoleProfilePage = lazy(() =>
  import('./pages/role/PublicRoleProfilePage').then((m) => ({ default: m.PublicRoleProfilePage })),
);

// Handle-scoped invite funnel (Pass C) — wraps existing invite stack
const HandleScopedInvitePage = lazy(() =>
  import('./pages/role/HandleScopedInvitePage').then((m) => ({
    default: m.HandleScopedInvitePage,
  })),
);

// Plain-profile creator recruiting landing (Phase 1G+) — /profile/:handle/creator
const ProfileCreatorInvitePage = lazy(() =>
  import('./pages/profile/ProfileCreatorInvitePage').then((m) => ({
    default: m.ProfileCreatorInvitePage,
  })),
);

// Account: Your Professional Roles section (Pass B)
const ProfessionalRolesPage = lazy(() =>
  import('./pages/account/ProfessionalRolesPage').then((m) => ({
    default: m.ProfessionalRolesPage,
  })),
);

// Network role access guard (Connector / Agent / General Agent)
import { RoleApprovedGuard } from './components/role/RoleApprovedGuard';

// Onboarding
const OnboardingPage = lazy(() =>
  import('./pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);

// Utility pages
const SimpleTest = lazy(() =>
  import('./pages/SimpleTest').then((m) => ({ default: m.SimpleTest })),
);
const TokenChecker = lazy(() =>
  import('./pages/TokenChecker').then((m) => ({ default: m.TokenChecker })),
);

// Suspense wrapper
const LazyPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense
    fallback={
      <div className="min-h-[50vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    }
  >
    {children}
  </Suspense>
);

// Canonical public BrandStore host.
//
// Katalog's public entity routes are not mounted as bare pages:
// signed-in users render inside the app DashboardLayout while guests receive
// the canonical public frame with horizontal containment and responsive gutters.
//
// Keep Kloutforce branding/navigation app-local while matching that host geometry.
const BrandStoreLayoutGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  if (authUser) {
    return (
      <DashboardLayout>
        <LazyPage>{children}</LazyPage>
      </DashboardLayout>
    );
  }

  return (
    <div className="h-[100dvh] overflow-hidden bg-background text-foreground flex flex-col">
      <AppMain>
        <LazyPage>{children}</LazyPage>
      </AppMain>
    </div>
  );
};

// 404 fallback
const NotFoundPage: React.FC = () => {
  const { authUser, loading } = useAuth();
  const location = useLocation();
  if (!loading && !authUser) {
    return <Navigate to={routes.auth} replace state={{ from: location.pathname }} />;
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-foreground mb-2">404</h1>
        <p className="text-muted-foreground">Page not found</p>
      </div>
    </div>
  );
};

// Root redirect
const RootRedirect: React.FC = () => {
  const { authUser, appProfile, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  if (!authUser)
    return (
      <LazyPage>
        <LandingPage />
      </LazyPage>
    );

  // ROLE-RETURN OVERRIDE: If a role-entry target exists, redirect there instead of /home
  let storedReturn: string | null = null;
  try {
    storedReturn = sessionStorage.getItem('kloutforce:authReturnTo');
  } catch {
    /* noop */
  }
  if (storedReturn && storedReturn.startsWith('/') && !storedReturn.startsWith('//')) {
    const isProgramRoute =
      storedReturn.startsWith('/agent') ||
      storedReturn.startsWith('/partner') ||
      storedReturn.startsWith('/general-agent') ||
      storedReturn.startsWith('/connector');
    if (isProgramRoute) {
      console.warn(
        '[ROLE RETURN OVERRIDE] RootRedirect overriding default with stored role-return:',
        storedReturn,
      );
      try {
        sessionStorage.removeItem('kloutforce:authReturnTo');
      } catch {
        /* noop */
      }
      return <Navigate to={storedReturn} replace />;
    }
  }

  if (!appProfile) {
    return <Navigate to={routes.onboarding} replace />;
  }
  return <Navigate to={routes.app.home} replace />;
};

// Redirect helper: /account/katalog/:katalogId[/dashboard|manage] → plural
const RedirectKatalogToPlural: React.FC<{ mode: 'dashboard' | 'manage' }> = ({ mode }) => {
  const { katalogId } = useParams<{ katalogId: string }>();
  if (!katalogId) return <Navigate to="/account/katalogs" replace />;
  return <Navigate to={`/account/katalogs/${katalogId}/${mode}`} replace />;
};

// Protected route with DashboardLayout shell
const ProtectedWithLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute redirectTo={routes.auth} onboardingPath={routes.onboarding} useGlobalProfile>
    <DashboardLayout>
      <LazyPage>{children}</LazyPage>
    </DashboardLayout>
  </ProtectedRoute>
);

// Program-specific protected route: auth required, onboarding NOT required
const ProtectedProgramRoute: React.FC<{ children: React.ReactNode; withLayout?: boolean }> = ({
  children,
  withLayout = true,
}) => (
  <ProtectedRoute redirectTo={routes.auth} requireOnboarding={false} useGlobalProfile>
    {withLayout ? (
      <DashboardLayout>
        <LazyPage>{children}</LazyPage>
      </DashboardLayout>
    ) : (
      <LazyPage>{children}</LazyPage>
    )}
  </ProtectedRoute>
);

// Auth-only guard: requires login but does NOT bounce onboarded users away from
// /onboarding/* paths. Use for guided account flows that must remain reachable
// after the user already has an app profile (e.g. /onboarding/katalog).
const AuthOnlyRoute: React.FC<{ children: React.ReactNode; withLayout?: boolean }> = ({
  children,
  withLayout = true,
}) => {
  const { authUser, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!authUser) {
    return <Navigate to={routes.auth} replace />;
  }
  return withLayout ? (
    <DashboardLayout>
      <LazyPage>{children}</LazyPage>
    </DashboardLayout>
  ) : (
    <LazyPage>{children}</LazyPage>
  );
};

// App Root
const App: React.FC = () => {
  useInviteIQCapture();
  useNoIndex();

  return (
    <div className="min-h-screen bg-background text-foreground" data-theme="kloutforce">
      <Routes>
        {/* ---------- PUBLIC ROUTES ---------- */}
        <Route path={routes.landing} element={<RootRedirect />} />
        <Route
          path={routes.auth}
          element={
            <LazyPage>
              <AuthPage />
            </LazyPage>
          }
        />
        <Route
          path={routes.authCallback}
          element={
            <LazyPage>
              <AuthCallbackPage />
            </LazyPage>
          }
        />
        <Route
          path="/invite/:token"
          element={
            <LazyPage>
              <OperatorInvitePage />
            </LazyPage>
          }
        />
        <Route
          path="/creator/invite/:code"
          element={
            <LazyPage>
              <OperatorInvitePage />
            </LazyPage>
          }
        />
        <Route
          path="/privacy"
          element={
            <LazyPage>
              <PrivacyPage />
            </LazyPage>
          }
        />
        <Route
          path="/terms"
          element={
            <LazyPage>
              <TermsPage />
            </LazyPage>
          }
        />
        <Route
          path={routes.brandChildStore}
          element={
            <BrandStoreLayoutGuard>
              <BrandStorePage />
            </BrandStoreLayoutGuard>
          }
        />
        <Route
          path={routes.brandStore}
          element={
            <BrandStoreLayoutGuard>
              <BrandStorePage />
            </BrandStoreLayoutGuard>
          }
        />

        {/* Utility */}
        <Route
          path="/simple-test"
          element={
            <LazyPage>
              <SimpleTest />
            </LazyPage>
          }
        />
        <Route
          path="/token-checker"
          element={
            <LazyPage>
              <TokenChecker />
            </LazyPage>
          }
        />
        <Route
          path="/tailwind-sanity"
          element={
            <LazyPage>
              <TailwindSanityPage />
            </LazyPage>
          }
        />

        {/* Canonical public Profile route — must precede /:handle bare-handle resolver. */}
        {/* /profile/:handle/creator — handle-based plain creator recruiting (Phase 1G+). */}
        <Route
          path="/profile/:handle/creator"
          element={
            <LazyPage>
              <ProfileCreatorInvitePage />
            </LazyPage>
          }
        />
        <Route
          path={routes.account.profilePublic}
          element={
            <DashboardLayout>
              <LazyPage>
                <CreatorProfilePage routeMode="profile-compat" />
              </LazyPage>
            </DashboardLayout>
          }
        />

        {/* ---------- ONBOARDING ---------- */}
        <Route
          path={routes.onboarding}
          element={
            <ProtectedRoute redirectTo={routes.auth} requireOnboarding={false} useGlobalProfile>
              <LazyPage>
                <OnboardingPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* ---------- MAIN APP ROUTES ---------- */}
        <Route
          path={routes.app.home}
          element={
            <ProtectedWithLayout>
              <HomePage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.discover}
          element={
            <ProtectedWithLayout>
              <DiscoverPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.creators}
          element={
            <ProtectedWithLayout>
              <CreatorDiscoveryPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.create}
          element={
            <ProtectedWithLayout>
              <CreatePage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.notifications}
          element={
            <ProtectedWithLayout>
              <NotificationsPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.katalog}
          element={
            <ProtectedWithLayout>
              <KatalogPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.dashboard}
          element={
            <ProtectedWithLayout>
              <CreatorDashboardPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.dealDetail}
          element={
            <ProtectedWithLayout>
              <DealDetailPage />
            </ProtectedWithLayout>
          }
        />

        {/* Account */}
        <Route
          path={routes.account.profile}
          element={
            <ProtectedWithLayout>
              <ProfilePage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.socials}
          element={
            <ProtectedWithLayout>
              <ConnectSocialsPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.portfolio}
          element={
            <ProtectedWithLayout>
              <PortfolioPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.earnings}
          element={
            <ProtectedWithLayout>
              <EarningsPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.wallet}
          element={
            <ProtectedWithLayout>
              <WalletPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.settings}
          element={
            <ProtectedWithLayout>
              <SettingsPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.performance}
          element={
            <ProtectedWithLayout>
              <PerformancePage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.deals}
          element={
            <ProtectedWithLayout>
              <BrandDealsPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.katalogs}
          element={
            <ProtectedWithLayout>
              <KatalogsListPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/katalogs/create"
          element={
            <ProtectedWithLayout>
              <KatalogCreatePage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/katalogs/:id/dashboard"
          element={
            <ProtectedWithLayout>
              <KatalogDashboardPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/katalogs/:id/manage"
          element={
            <ProtectedWithLayout>
              <KatalogEditorPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/katalogs/:id/edit"
          element={
            <ProtectedWithLayout>
              <KatalogEditorPage />
            </ProtectedWithLayout>
          }
        />

        {/* Legacy singular Katalag routes — keep YourKatalogPage at /account/katalog (feed surface) but redirect editor/manage variants to plural */}
        <Route
          path="/account/katalog"
          element={
            <ProtectedWithLayout>
              <YourKatalogPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/katalog/create"
          element={<Navigate to="/account/katalogs/create" replace />}
        />
        <Route
          path="/account/katalog/manage"
          element={<Navigate to="/account/katalogs" replace />}
        />
        <Route
          path="/account/katalog/:katalogId/dashboard"
          element={<RedirectKatalogToPlural mode="dashboard" />}
        />
        <Route
          path="/account/katalog/:katalogId/manage"
          element={<RedirectKatalogToPlural mode="manage" />}
        />
        <Route
          path={routes.account.katalogEditor}
          element={<RedirectKatalogToPlural mode="manage" />}
        />
        <Route
          path={routes.account.invites}
          element={
            <ProtectedWithLayout>
              <InvitesPage />
            </ProtectedWithLayout>
          }
        />
        {/* Legacy alias — redirect to unified Invites with Customers tab. */}
        <Route
          path={routes.account.invited}
          element={<Navigate to={`${routes.account.invites}?tab=customers`} replace />}
        />
        {/* Guided first-Katalag setup. Auth required, no creator onboarding gate. */}
        <Route
          path="/onboarding/katalog"
          element={
            <AuthOnlyRoute>
              <KatalogOnboardingPage />
            </AuthOnlyRoute>
          }
        />
        <Route
          path={routes.account.professionalRoles}
          element={
            <ProtectedWithLayout>
              <ProfessionalRolesPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.invite}
          element={
            <ProtectedWithLayout>
              <InvitePage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.instructions}
          element={
            <ProtectedWithLayout>
              <CreatorInstructionsPage />
            </ProtectedWithLayout>
          }
        />

        {/* ---------- GENERAL AGENT ROUTES ---------- */}
        {/* Public */}
        <Route
          path={routes.generalAgent.landing}
          element={
            <LazyPage>
              <GALandingPage />
            </LazyPage>
          }
        />
        <Route
          path="/general-agent/invite/:code"
          element={
            <LazyPage>
              <GAInvitePage />
            </LazyPage>
          }
        />
        {/* Auth required, no creator onboarding */}
        <Route
          path={routes.generalAgent.request}
          element={
            <ProtectedProgramRoute withLayout={false}>
              <GARequestPage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.generalAgent.welcome}
          element={
            <ProtectedProgramRoute withLayout={false}>
              <GAWelcomePage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.generalAgent.dashboard}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="general_agent">
                <GADashboardPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.generalAgent.earnings}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="general_agent">
                <GAEarningsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.generalAgent.recruits}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="general_agent">
                <GARecruitsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.generalAgent.payouts}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="general_agent">
                <GAPayoutsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.generalAgent.inviteTools}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="general_agent">
                <GAInviteToolsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.generalAgent.instructions}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="general_agent">
                <GAInstructionsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.generalAgent.manage}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="general_agent">
                <GAManageProfilePage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path="/general-agent/activate"
          element={
            <ProtectedProgramRoute withLayout={false}>
              <GAActivatePage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.generalAgent.activationReturn}
          element={<Navigate to={routes.generalAgent.dashboard} replace />}
        />

        {/* ---------- AGENT ROUTES ---------- */}
        {/* Public */}
        <Route
          path={routes.agent.landing}
          element={
            <LazyPage>
              <AgentLandingPage />
            </LazyPage>
          }
        />
        <Route
          path="/agent/invite/:code"
          element={
            <LazyPage>
              <AgentInviteLandingPage />
            </LazyPage>
          }
        />
        {/* Auth required, no creator onboarding */}
        <Route
          path={routes.app.agentApply}
          element={
            <ProtectedProgramRoute>
              <AgentApplyPage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path="/agent/activate"
          element={
            <ProtectedProgramRoute withLayout={false}>
              <AgentActivatePage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.agent.welcome}
          element={
            <ProtectedProgramRoute withLayout={false}>
              <AgentWelcomePage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.agent.dashboard}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="agent">
                <AgentOverviewPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.agent.network}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="agent">
                <AgentNetworkPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.agent.recruits}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="agent">
                <AgentRecruitsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path="/agent/recruits/:recruitmentId/fee"
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="agent">
                <AgentManageBrandFeePage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path="/agent/billing"
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="agent">
                <AgentBillingPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path="/agent/ga-agreements"
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="agent">
                <AgentGAAgreementsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.agent.earnings}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="agent">
                <AgentEarningsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.agent.payouts}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="agent">
                <AgentPayoutsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.agent.inviteTools}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="agent">
                <AgentInviteToolsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.agent.instructions}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="agent">
                <AgentInstructionsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.agent.manage}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="agent">
                <AgentManageProfilePage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.agent.activationReturn}
          element={
            <ProtectedProgramRoute withLayout={false}>
              <ActivationReturnPage />
            </ProtectedProgramRoute>
          }
        />
        {/* Legacy redirects */}
        <Route
          path={routes.agent.overview}
          element={<Navigate to={routes.agent.dashboard} replace />}
        />
        <Route path="/agent/invite" element={<Navigate to={routes.agent.inviteTools} replace />} />

        {/* ---------- PARTNER ROUTES ---------- */}
        {/* Public */}
        <Route
          path={routes.partner.landing}
          element={
            <LazyPage>
              <PartnerLandingPage />
            </LazyPage>
          }
        />
        <Route
          path="/partner/invite/:code"
          element={
            <LazyPage>
              <PartnerInviteLandingPage />
            </LazyPage>
          }
        />
        {/* Auth required, no creator onboarding */}
        <Route
          path={routes.partner.onboarding}
          element={
            <ProtectedProgramRoute withLayout={false}>
              <PartnerOnboardingPage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path="/partner/activate"
          element={
            <ProtectedProgramRoute withLayout={false}>
              <PartnerActivatePage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.partner.upgrade}
          element={
            <ProtectedProgramRoute>
              <PartnerUpgradePage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.partner.welcome}
          element={
            <ProtectedProgramRoute withLayout={false}>
              <PartnerWelcomePage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.partner.dashboard}
          element={
            <ProtectedProgramRoute>
              <PartnerDashboardPage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.partner.earnings}
          element={
            <ProtectedProgramRoute>
              <PartnerEarningsPage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.partner.network}
          element={
            <ProtectedProgramRoute>
              <PartnerNetworkPage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.partner.recruits}
          element={
            <ProtectedProgramRoute>
              <PartnerRecruitsPage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.partner.payouts}
          element={
            <ProtectedProgramRoute>
              <PartnerPayoutsPage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.partner.inviteTools}
          element={
            <ProtectedProgramRoute>
              <PartnerInviteToolsPage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.partner.instructions}
          element={
            <ProtectedProgramRoute>
              <PartnerInstructionsPage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.partner.manage}
          element={
            <ProtectedProgramRoute>
              <PartnerManageProfilePage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.partner.activationReturn}
          element={
            <ProtectedProgramRoute withLayout={false}>
              <ActivationReturnPage />
            </ProtectedProgramRoute>
          }
        />

        {/* ---------- CONNECTOR ROUTES ---------- */}
        {/* Public */}
        <Route
          path={routes.connector.landing}
          element={
            <LazyPage>
              <ConnectorLandingPage />
            </LazyPage>
          }
        />
        <Route
          path="/connector/invite/:code"
          element={
            <LazyPage>
              <ConnectorInviteLandingPage />
            </LazyPage>
          }
        />
        {/* Auth required, no creator onboarding */}
        <Route
          path={routes.connector.onboarding}
          element={
            <ProtectedProgramRoute withLayout={false}>
              <ConnectorOnboardingPage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.connector.welcome}
          element={
            <ProtectedProgramRoute withLayout={false}>
              <ConnectorWelcomePage />
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.connector.dashboard}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="connector">
                <ConnectorDashboardPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.connector.earnings}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="connector">
                <ConnectorEarningsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.connector.recruits}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="connector">
                <ConnectorRecruitsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.connector.payouts}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="connector">
                <ConnectorPayoutsPage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />
        <Route
          path={routes.connector.manage}
          element={
            <ProtectedProgramRoute>
              <RoleApprovedGuard role="connector">
                <ConnectorManageProfilePage />
              </RoleApprovedGuard>
            </ProtectedProgramRoute>
          }
        />

        {/* Redirects */}
        <Route path="/account" element={<Navigate to={routes.account.profile} replace />} />

        {/* ---------- PUBLIC ROLE PROFILE PAGES (Pass B) ---------- */}
        {/* Resolved from role_handles. Static reserved sub-paths above (e.g. */}
        {/* /connector/onboarding, /agent/apply) win via React Router specificity. */}
        {/* Handle-scoped invite funnels (Pass C) MUST come BEFORE the /:handle */}
        {/* profile route to win specificity. They resolve the inviter via */}
        {/* role_handles and hand off into the canonical invite stack. */}
        <Route
          path="/connector/:handle/invite/:target"
          element={
            <LazyPage>
              <HandleScopedInvitePage inviterRole="connector" />
            </LazyPage>
          }
        />
        <Route
          path="/general-agent/:handle/invite/:target"
          element={
            <LazyPage>
              <HandleScopedInvitePage inviterRole="general_agent" />
            </LazyPage>
          }
        />
        <Route
          path="/agent/:handle/invite/:target"
          element={
            <LazyPage>
              <HandleScopedInvitePage inviterRole="agent" />
            </LazyPage>
          }
        />
        <Route
          path="/partner/:handle/invite/:target"
          element={
            <LazyPage>
              <HandleScopedInvitePage inviterRole="partner" />
            </LazyPage>
          }
        />

        {/* Clean handle-scoped invite URLs (primary share surface). */}
        {/* /<role>/:handle/:target — same page, same token mint, no /invite/ segment. */}
        {/* Legacy /<role>/:handle/invite/:target above remains valid for back-compat. */}
        {/* Edge validation is performed inside HandleScopedInvitePage against */}
        {/* INVITE_EDGE_MATRIX, so unknown :target values render an "invalid-edge" state. */}
        <Route
          path="/connector/:handle/:target"
          element={
            <LazyPage>
              <HandleScopedInvitePage inviterRole="connector" />
            </LazyPage>
          }
        />
        <Route
          path="/general-agent/:handle/:target"
          element={
            <LazyPage>
              <HandleScopedInvitePage inviterRole="general_agent" />
            </LazyPage>
          }
        />
        <Route
          path="/agent/:handle/:target"
          element={
            <LazyPage>
              <HandleScopedInvitePage inviterRole="agent" />
            </LazyPage>
          }
        />
        <Route
          path="/partner/:handle/:target"
          element={
            <LazyPage>
              <HandleScopedInvitePage inviterRole="partner" />
            </LazyPage>
          }
        />

        <Route
          path="/connector/:handle"
          element={
            <LazyPage>
              <PublicRoleProfilePage role="connector" />
            </LazyPage>
          }
        />
        <Route
          path="/general-agent/:handle"
          element={
            <LazyPage>
              <PublicRoleProfilePage role="general_agent" />
            </LazyPage>
          }
        />
        <Route
          path="/agent/:handle"
          element={
            <LazyPage>
              <PublicRoleProfilePage role="agent" />
            </LazyPage>
          }
        />
        <Route
          path="/partner/:handle"
          element={
            <LazyPage>
              <PublicRoleProfilePage role="partner" />
            </LazyPage>
          }
        />

        {/* ========== HANDLE-BASED ROUTES (catch-all — must be last) ========== */}
        <Route
          path="/:handle/deals/:id"
          element={
            <LazyPage>
              <DealDetailPage />
            </LazyPage>
          }
        />
        <Route
          path="/:handle/:slug"
          element={
            <DashboardLayout>
              <LazyPage>
                <CreatorProfilePage routeMode="canonical-katalog" />
              </LazyPage>
            </DashboardLayout>
          }
        />
        <Route
          path="/:handle"
          element={
            <DashboardLayout>
              <LazyPage>
                <CreatorProfilePage routeMode="canonical-katalog" />
              </LazyPage>
            </DashboardLayout>
          }
        />

        {/* 404 fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
};

export default App;
